<script lang="ts">
  // One KPI: label, big value, optional change vs. the previous period, hint.
  //   <Stat card label="Uren" value="32:15" current={m} previous={pm} hint="vs. vorige maand" />
  // No icons: the label says what it is.
  // Put several in a <StatGrid>. `card` draws the card around it (instead of
  // wrapping it in a .card yourself). The value shrinks with the available
  // width (container query), so long amounts never get cut off.
  import type { Snippet } from 'svelte';
  import Delta from './Delta.svelte';
  import Skeleton from './Skeleton.svelte';

  interface Props {
    label: string;
    value: string;
    /** Value of this and the comparison period, for the ▲▼ delta. */
    current?: number;
    previous?: number | null;
    /** For costs etc.: going down is good. */
    invert?: boolean;
    hint?: string;
    loading?: boolean;
    /** Render as a card (border, padding, background). */
    card?: boolean;
    /** Highlighted card (brand edge), e.g. the KPI that matters most. */
    highlight?: boolean;
    size?: 'sm' | 'md';
    /** Makes the whole stat a link. */
    href?: string;
    footer?: Snippet;
  }
  let { label, value, current, previous = null, invert = false, hint, loading = false, card = false, highlight = false, size = 'md', href, footer }: Props = $props();
</script>

<svelte:element this={href ? 'a' : 'div'} {href} class="stat {size}" class:card class:highlight class:link={!!href}>
  <div class="top">
    <span class="label">{label}</span>
  </div>
  {#if loading}
    <Skeleton width="60%" height="30px" />
  {:else}
    <div class="value tabular">{value}</div>
  {/if}
  {#if (!loading && current !== undefined && previous !== null && previous !== undefined) || hint}
    <div class="meta">
      {#if !loading && current !== undefined}<Delta {current} {previous} {invert} />{/if}
      {#if hint}<span class="hint">{hint}</span>{/if}
    </div>
  {/if}
  {#if footer}{@render footer()}{/if}
</svelte:element>

<style>
  .stat {
    display: flex;
    flex-direction: column;
    gap: var(--space-1_5);
    min-width: 0;
    height: 100%;
    container-type: inline-size;
    color: inherit;
  }
  .card {
    padding: var(--card-pad);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-xs);
  }
  .card.highlight { border-color: var(--border-brand); }
  .link { text-decoration: none; transition: border-color var(--dur), box-shadow var(--dur); }
  .link:hover { text-decoration: none; }
  .link.card:hover { border-color: var(--border-strong); box-shadow: var(--shadow-sm); }
  .link:focus-visible { outline: none; box-shadow: var(--ring); border-radius: var(--radius); }
  .top { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); min-height: 20px; }
  .label {
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    color: var(--text-2);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .value {
    font-size: clamp(var(--text-lg), 15cqi, var(--text-2xl));
    font-weight: var(--weight-semibold);
    letter-spacing: var(--tracking-tight);
    line-height: var(--leading-tight);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--text);
  }
  .sm .value { font-size: clamp(var(--text-md), 12cqi, var(--text-xl)); }
  .meta { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; min-height: 20px; }
  .hint { font-size: var(--text-xs); color: var(--text-3); min-width: 0; }
</style>
